// TaxMasterPercent.jsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../components/HeaderBar";
import getCurrentSkin from "../utils/getCurrentSkin";
import { ThemeContext } from "../context/ThemeContext";
import AdminLayoutWrapper from "../components/AdminLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  HelpCircle,
  RefreshCw,
  Save,
  Search,
  Send,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

/**
 * Tax Percentage Master (ASPX -> React)
 * Tables:
 * - tbl_tax_mas (dropdown)
 * - tbl_tax_percentagemas (grid + insert + approve/disapprove bulk submit)
 *
 * APIs:
 * GET  /api/tax-percent/taxes
 * GET  /api/tax-percent/list?page&limit&q&taxId
 * POST /api/tax-percent/create
 * POST /api/tax-percent/submit
 */
const TaxMasterPercent = () => {
  const BASE_URL = "https://nissi-india.com";

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = useMemo(() => `bg-${baseColor}-${baseShade}`, [baseColor, baseShade]);
  const hoverClass = useMemo(() => `hover:bg-${baseColor}-700`, [baseColor]);
  const navigate = useNavigate();

const handleBack = () => {
  if (saving) return;
  navigate(-1);
};

  const [showForm, setShowForm] = useState(true);

  // finyear header (same pattern)
  const finYear = (localStorage.getItem("finYear") || "").trim(); // if you use finYear
  const headers = useMemo(() => (finYear ? { "x-finyear": finYear } : {}), [finYear]);

  // Dropdown taxes
  const [taxes, setTaxes] = useState([]);
  const [taxId, setTaxId] = useState("Select");
  const [taxPercentage, setTaxPercentage] = useState("");

  // Search + filter
  const [q, setQ] = useState("");
  const [filterTaxId, setFilterTaxId] = useState("All");

  // Grid
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // Paging
  const recordsPerPage = 20;
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  // Actions
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchTaxes = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/tax-percent/taxes`, { headers });
      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to load taxes");
        return;
      }
      setTaxes(res.data.list || []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load taxes");
    }
  };

  const fetchList = async (p = 1, opts = {}) => {
    const nextQ = opts.q ?? q;
    const nextFilterTaxId = opts.filterTaxId ?? filterTaxId;

    try {
      setLoading(true);

      const params = {
        page: p,
        limit: recordsPerPage,
      };

      if (String(nextQ || "").trim()) params.q = String(nextQ).trim();
      if (nextFilterTaxId && nextFilterTaxId !== "All") params.taxId = Number(nextFilterTaxId);

      const res = await axios.get(`${BASE_URL}/api/tax-percent/list`, { params, headers });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to load list");
        return;
      }

      const list = (res.data.list || []).map((r) => ({
        ...r,
        _approve: !!r.fld_Approve,
        _disapprove: !!r.fld_DisApprove,
      }));

      setRows(list);
      setPage(res.data.page || p);
      setTotalPages(res.data.totalPages || 1);
      setTotalRecords(res.data.total || 0);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTaxes();
    fetchList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validate = () => {
    if (taxId === "Select") {
      toast.error("Please select Tax Name..");
      return false;
    }
    if (!String(taxPercentage || "").trim()) {
      toast.error("Please enter Tax Percentage..");
      return false;
    }
    const n = Number(String(taxPercentage).trim());
    if (!Number.isFinite(n) || n < 0 || n > 99) {
      toast.error("Tax Percentage must be between 0 and 99");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      setSaving(true);

      const res = await axios.post(
        `${BASE_URL}/api/tax-percent/create`,
        {
          taxId: Number(taxId),
          percentage: String(taxPercentage).trim(),
        },
        { headers }
      );

      if (!res.data?.success) {
        toast.error(res.data?.message || "Already Exists");
        return;
      }

      toast.success(res.data?.message || "Successfully Inserted");
      setTaxPercentage("");
      // refresh list to first page
      await fetchList(1);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.message || "Server error");
    } finally {
      setSaving(false);
    }
  };

  const toggleRow = (idx, key, checked) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [key]: checked } : r)));
  };

  const setAllColumn = (key, checked) => {
    setRows((prev) => prev.map((r) => ({ ...r, [key]: checked })));
  };

  const isAllChecked = (key) => {
    if (!rows.length) return false;
    return rows.every((r) => !!r[key]);
  };

  const handleSubmit = async () => {
    // Must select at least one row (approve/disapprove checked)
    const payloadRows = rows
      .filter((r) => r._approve || r._disapprove)
      .map((r) => ({
        taxPercentId: r.fld_taxpercentid,
        approve: r._approve ? 1 : 0,
        disapprove: r._disapprove ? 1 : 0,
      }));

    if (payloadRows.length === 0) {
      toast.error("Please select atleast one");
      return;
    }

    try {
      setSubmitting(true);

      const res = await axios.post(
        `${BASE_URL}/api/tax-percent/submit`,
        { rows: payloadRows },
        { headers }
      );

      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed. Please retry");
        return;
      }

      toast.success(res.data?.message || "Saved successfully");
      await fetchList(page);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.message || "Server error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRefresh = async () => {
    setTaxId("Select");
    setTaxPercentage("");
    setQ("");
    setFilterTaxId("All");
    await fetchTaxes();
    await fetchList(1, { q: "", filterTaxId: "All" });
    toast.info("Refreshed");
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      const start = Math.max(1, page - 2);
      const end = Math.min(totalPages, start + 4);

      for (let i = start; i <= end; i++) pages.push(i);
      if (end < totalPages) pages.push("...");
    }

    return pages;
  };

  const handlePageChange = (p) => {
    if (!p || p === "..." || p === page) return;
    fetchList(p);
  };

  const handleSearch = async () => {
    await fetchList(1, { q, filterTaxId });
  };

  return (
    <AdminLayoutWrapper>
      <div className={`min-h-screen ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">Tax Percentage Master</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* Header row */}
          <div className="flex justify-between items-center gap-3 flex-wrap mb-6">
            <div className="flex items-center gap-4">
              <button
      type="button"
      onClick={handleBack}
      disabled={saving}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
        isDark
          ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
          : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
      } disabled:opacity-60`}
    >
      <ChevronLeft className="w-5 h-5" />
      Back
    </button>
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Tax Percentage Master
              </h1>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {totalRecords} Records
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => window.open("/admin/help/tax-percent", "_blank")}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow hover:shadow-md transition ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-800 border border-gray-300"
                }`}
              >
                <HelpCircle className="w-5 h-5" />
                Help
              </button>

              <button
                type="button"
                onClick={handleRefresh}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow hover:shadow-md transition ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-800 border border-gray-300"
                }`}
              >
                <RefreshCw className="w-5 h-5" />
                Refresh
              </button>

              <button
                onClick={() => setShowForm(!showForm)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showForm ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                {showForm ? "Hide Entry Form" : "Show Entry Form"}
              </button>
            </div>
          </div>

          {/* Entry Form */}
          <div className={`rounded-xl border mb-6 overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            {showForm && (
              <div className={`${isDark ? "bg-gray-800" : "bg-gray-50"} p-6`}>
                <div className="grid grid-cols-12 gap-4 items-center max-w-4xl">
                  {/* Tax Name */}
                  <div className="col-span-12 md:col-span-6">
                    <label className="text-sm font-medium block mb-1">
                      Tax Name <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={taxId}
                      onChange={(e) => setTaxId(e.target.value)}
                      disabled={saving || submitting}
                      className={`w-full px-3 py-2 rounded-lg border ${
                        isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-black"
                      }`}
                    >
                      <option value="Select">Select</option>
                      {taxes.map((t) => (
                        <option key={t.fld_taxid} value={t.fld_taxid}>
                          {t.fld_taxname}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Tax Percentage */}
                  <div className="col-span-12 md:col-span-6">
                    <label className="text-sm font-medium block mb-1">
                      Tax Percentage <span className="text-red-500">*</span>
                    </label>
                    <input
                      value={taxPercentage}
                      onChange={(e) => {
                        // only digits, max 2 chars (ASPX MaxLength=2)
                        const v = e.target.value.replace(/[^\d]/g, "").slice(0, 2);
                        setTaxPercentage(v);
                      }}
                      disabled={saving || submitting}
                      className={`w-full px-3 py-2 rounded-lg border ${
                        isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-black"
                      }`}
                      placeholder="Enter % (0-99)"
                      inputMode="numeric"
                    />
                  </div>

                  <div className="col-span-12 flex justify-end mt-2">
                    <button
                      type="button"
                      onClick={handleSave}
                      disabled={saving || submitting}
                      className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white disabled:opacity-60`}
                    >
                      <Save className="w-5 h-5" />
                      {saving ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Filters */}
          <div className={`rounded-xl border mb-6 overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            <div className={`${isDark ? "bg-gray-800" : "bg-gray-50"} p-6`}>
              <div className="grid grid-cols-12 gap-4 items-end">
                {/* Search */}
                <div className="col-span-12 md:col-span-6">
                  <label className="text-sm font-medium block mb-1">Search</label>
                  <div className="flex items-center gap-2">
                    <div
                      className={`flex items-center gap-2 w-full px-3 py-2 rounded-lg border ${
                        isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-black"
                      }`}
                    >
                      <Search className="w-4 h-4 opacity-70" />
                      <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        className="w-full bg-transparent outline-none"
                        placeholder="Search tax name / percentage..."
                      />
                    </div>
                  </div>
                </div>

                {/* Tax Filter */}
                <div className="col-span-12 md:col-span-4">
                  <label className="text-sm font-medium block mb-1">Filter by Tax</label>
                  <select
                    value={filterTaxId}
                    onChange={(e) => setFilterTaxId(e.target.value)}
                    className={`w-full px-3 py-2 rounded-lg border ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-black"
                    }`}
                  >
                    <option value="All">All</option>
                    {taxes.map((t) => (
                      <option key={t.fld_taxid} value={t.fld_taxid}>
                        {t.fld_taxname}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Search Button */}
                <div className="col-span-12 md:col-span-2 flex justify-end">
                  <button
                    type="button"
                    onClick={handleSearch}
                    disabled={loading}
                    className={`flex items-center gap-2 px-5 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      isDark ? "bg-gray-700 hover:bg-gray-600 text-white" : `${buttonClass} ${hoverClass} text-white`
                    } disabled:opacity-60`}
                  >
                    <Search className="w-5 h-5" />
                    {loading ? "Searching..." : "Search"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            <div className="overflow-x-auto max-w-full">
              <div className="max-h-[60vh] overflow-y-auto">
                <table className="min-w-[950px] w-full">
                  <thead className={`${buttonClass} text-white sticky top-0 z-10`}>
                    <tr>
                      <th className="px-3 py-3 text-center font-semibold border-r border-white/20 whitespace-nowrap">
                        S.No
                      </th>
                      <th className="px-3 py-3 text-center font-semibold border-r border-white/20 whitespace-nowrap">
                        Tax Name
                      </th>
                      <th className="px-3 py-3 text-center font-semibold border-r border-white/20 whitespace-nowrap">
                        Tax %
                      </th>

                      {/* Approve header with select all */}
                      <th className="px-3 py-3 text-center font-semibold border-r border-white/20 whitespace-nowrap">
                        <div className="flex items-center justify-center gap-2">
                          <span>Approve</span>
                          <input
                            type="checkbox"
                            checked={isAllChecked("_approve")}
                            onChange={(e) => setAllColumn("_approve", e.target.checked)}
                            className="w-4 h-4 cursor-pointer accent-white"
                            title="Select all Approve"
                          />
                        </div>
                      </th>

                      {/* DisApprove header with select all */}
                      <th className="px-3 py-3 text-center font-semibold whitespace-nowrap">
                        <div className="flex items-center justify-center gap-2">
                          <span>DisApprove</span>
                          <input
                            type="checkbox"
                            checked={isAllChecked("_disapprove")}
                            onChange={(e) => setAllColumn("_disapprove", e.target.checked)}
                            className="w-4 h-4 cursor-pointer accent-white"
                            title="Select all DisApprove"
                          />
                        </div>
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {loading ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-6 text-center opacity-70">
                          Loading...
                        </td>
                      </tr>
                    ) : rows.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-6 text-center opacity-70">
                          No Records Found
                        </td>
                      </tr>
                    ) : (
                      rows.map((r, idx) => (
                        <tr
                          key={r.fld_taxpercentid}
                          className={`transition-colors duration-200 ${
                            idx % 2 === 0
                              ? isDark
                                ? "bg-gray-800"
                                : "bg-gray-50"
                              : isDark
                              ? "bg-gray-900"
                              : "bg-white"
                          }`}
                        >
                          <td className="px-3 py-3 text-sm text-center border-r border-gray-200 dark:border-gray-700">
                            {(page - 1) * recordsPerPage + (idx + 1)}
                          </td>

                          <td className="px-3 py-3 text-sm text-center border-r border-gray-200 dark:border-gray-700">
                            {r.fld_taxname}
                          </td>

                          <td className="px-3 py-3 text-sm text-center border-r border-gray-200 dark:border-gray-700">
                            {r.fld_taxpercentage}
                          </td>

                          <td className="px-3 py-3 text-center border-r border-gray-200 dark:border-gray-700">
                            <input
                              type="checkbox"
                              checked={!!r._approve}
                              onChange={(e) => toggleRow(idx, "_approve", e.target.checked)}
                              className={`w-4 h-4 cursor-pointer ${isDark ? "accent-blue-400" : "accent-blue-600"}`}
                            />
                          </td>

                          <td className="px-3 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={!!r._disapprove}
                              onChange={(e) => toggleRow(idx, "_disapprove", e.target.checked)}
                              className={`w-4 h-4 cursor-pointer ${isDark ? "accent-blue-400" : "accent-blue-600"}`}
                            />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Footer Submit + Pagination */}
            <div className={`flex flex-col md:flex-row items-center justify-between gap-3 p-4 border-t ${isDark ? "border-gray-700" : "border-gray-200"}`}>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting || loading}
                className={`inline-flex items-center gap-2 px-5 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white disabled:opacity-60`}
                title="Submit approve/disapprove changes"
              >
                <Send className="w-5 h-5" />
                {submitting ? "Submitting..." : "Submit"}
              </button>

              {/* Pagination */}
              <div className="flex items-center gap-2 flex-wrap justify-center">
                <button
                  type="button"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page <= 1 || loading}
                  className={`px-3 py-2 rounded-lg border text-sm ${
                    isDark
                      ? "border-gray-700 bg-gray-800 text-white disabled:opacity-50"
                      : "border-gray-300 bg-white text-gray-900 disabled:opacity-50"
                  }`}
                >
                  Prev
                </button>

                {renderPageNumbers().map((p, i) => (
                  <button
                    key={`${p}-${i}`}
                    type="button"
                    onClick={() => handlePageChange(p)}
                    disabled={p === "..." || loading}
                    className={`px-3 py-2 rounded-lg border text-sm ${
                      p === page
                        ? `${buttonClass} text-white border-transparent`
                        : isDark
                        ? "border-gray-700 bg-gray-800 text-white"
                        : "border-gray-300 bg-white text-gray-900"
                    } ${p === "..." ? "cursor-default opacity-70" : ""}`}
                  >
                    {p}
                  </button>
                ))}

                <button
                  type="button"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page >= totalPages || loading}
                  className={`px-3 py-2 rounded-lg border text-sm ${
                    isDark
                      ? "border-gray-700 bg-gray-800 text-white disabled:opacity-50"
                      : "border-gray-300 bg-white text-gray-900 disabled:opacity-50"
                  }`}
                >
                  Next
                </button>

                <div className={`text-xs ${isDark ? "text-white/70" : "text-gray-600"} ml-2`}>
                  Page {page} / {totalPages}
                </div>
              </div>
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </AdminLayoutWrapper>
  );
};

export default TaxMasterPercent;
